import type { DBSchema, IDBPDatabase, IDBPTransaction, OpenDBCallbacks, StoreKey, StoreNames, StoreValue } from 'idb';
import { openDB } from 'idb';
import type { DBClient, DBClientOptions, RWMode } from './db-types';

/**
 * Creates (and lazily opens) a typed IndexedDB client with built‑in versioned
 * migration handling, lifecycle callbacks, and convenience helpers for common
 * CRUD and transactional operations.
 *
 * The database is opened on first need (e.g. the first tx / put / get call).
 *
 * Migrations are executed sequentially for each intermediate version during
 * an upgrade. Any migration (or onUpgrade) error aborts the entire upgrade.
 *
 * A versionchange event automatically closes the old connection to unblock
 * pending upgrades. Termination resets the internal instance/promise state.
 *
 * @typeParam S - The IDB schema definition (extends DBSchema) used to derive
 *                strongly typed store names, keys, and values.
 *
 * @param options.name - The database name.
 * @param options.version - Target schema version; drives migration execution.
 * @param options.migrations - Map/object indexed by version number; each entry
 *                             is an async function invoked in ascending order
 *                             for every version greater than the old version
 *                             up to (and including) the target version.
 * @param options.onUpgrade - Optional callback invoked after all migrations
 *                            for a given open/upgrade cycle complete.
 * @param options.onBlocked - Fired when an upgrade cannot proceed because
 *                            other open tabs/contexts still hold older versions.
 * @param options.onBlocking - Fired when this open connection blocks a future
 *                             upgrade (another context is trying to upgrade).
 * @param options.onTerminate - Fired when the underlying connection is forcibly
 *                              terminated by the browser/user agent.
 * @param options.logPrefix - Optional log prefix string (default: "[DB]").
 *
 * @returns A DB client object exposing:
 *          - open(): Promise<IDBPDatabase<S>>
 *          - isOpen(): boolean
 *          - getDB(): IDBPDatabase<S> | undefined
 *          - close(): void
 *          - put(), get(), delete(), clear(), getAllKeys(), getAll()
 *          - tx(): typed transactional helper supporting single/multiple stores
 *
 * @throws Propagates any migration or onUpgrade errors (aborting the upgrade).
 *
 * @example
 * const db = createDBClient({
 *   name: 'app-db',
 *   version: 3,
 *   migrations: {
 *     1: async (db, tx) => { db.createObjectStore('users', { keyPath: 'id' }); },
 *     2: async (db, tx) => { db.createObjectStore('settings'); },
 *     3: async (db, tx) => { db.createObjectStore('logs', { autoIncrement: true }); }
 *   }
 * });
 *
 * await db.put('users', { id: 'u1', name: 'Alice' });
 * const user = await db.get('users', 'u1');
 * await db.tx(['users', 'logs'], 'readwrite', async (trx) => {
 *   await trx.objectStore('logs').add({ ts: Date.now(), evt: 'read-user' });
 * });
 */
export function createDBClient<S extends DBSchema>(options: DBClientOptions<S>): DBClient<S> {
  const { name, version, migrations, onUpgrade, onBlocked, onBlocking, onTerminate, logPrefix = '[DB]' } = options;

  let dbInstance: IDBPDatabase<S> | undefined = undefined;
  let dbPromise: Promise<IDBPDatabase<S>> | undefined = undefined;

  const _runMigrations = async (
    db: IDBPDatabase<S>,
    oldVersion: number,
    newVersion: number | null,
    tx: IDBPTransaction<S, StoreNames<S>[], 'versionchange'>
  ) => {
    const target = newVersion ?? version;
    console.debug(`${logPrefix} running migrations from ${oldVersion} to ${target}`);
    for (let v = oldVersion + 1; v <= target; v++) {
      const migration = migrations[v];
      if (migration) {
        console.debug(`${logPrefix} running migration for version ${v}`);
        try {
          await migration(db, tx);
        } catch (error) {
          console.error(`${logPrefix} migration error for version ${v}`, error);
          throw error; // abort the upgrade
        }
      } else {
        throw new Error(`${logPrefix} migration not found for version ${v}`);
      }
    }
  };

  const _callbacks: OpenDBCallbacks<S> = {
    async upgrade(db, oldVersion, newVersion, transaction, event) {
      console.debug(`${logPrefix} upgrade from ${oldVersion} -> ${newVersion}`);
      try {
        await _runMigrations(db, oldVersion, newVersion, transaction);
        await onUpgrade?.(db, oldVersion, newVersion, transaction, event);
      } catch (error) {
        console.error(`${logPrefix} migration/onUpgrade error`, error);
        throw error; // abort the upgrade
      }
    },
    blocked(currentVersion, blockedVersion, event) {
      const e = event as unknown as IDBVersionChangeEvent;
      console.warn(`${logPrefix} open blocked`, e);
      onBlocked?.(currentVersion, blockedVersion, e);
    },
    blocking(currentVersion, blockedVersion, event) {
      const e = event as unknown as IDBVersionChangeEvent;
      console.warn(`${logPrefix} blocking future upgrade`, e);
      onBlocking?.(currentVersion, blockedVersion, e);
    },
    terminated() {
      console.error(`${logPrefix} connection terminated`);
      dbInstance = undefined;
      dbPromise = undefined;
      onTerminate?.();
    },
  };

  async function open(): Promise<IDBPDatabase<S>> {
    if (dbPromise) return dbPromise;

    dbPromise = openDB<S>(name, version, _callbacks)
      .then((db) => {
        db.onversionchange = () => {
          console.warn(`${logPrefix} version change detected, closing old connection to unblock upgrade`);
          db.close();
          if (dbInstance === db) {
            dbInstance = undefined;
            dbPromise = undefined;
          }
        };

        dbInstance = db;
        return db;
      })
      .catch((error) => {
        console.error(`${logPrefix} openDB error`, error);
        dbPromise = undefined;
        throw error;
      });

    return dbPromise;
  }

  function isOpen(): boolean {
    return !!dbInstance;
  }

  function getDB(): IDBPDatabase<S> | undefined {
    return dbInstance;
  }

  function close(): void {
    dbInstance?.close();
    dbInstance = undefined;
    dbPromise = undefined;
  }

  // Overload: single store
  async function tx<R, N extends StoreNames<S>, M extends RWMode>(
    stores: N,
    mode: M,
    runner: (transaction: IDBPTransaction<S, [N], M>) => Promise<R>
  ): Promise<R>;
  // Overload: multiple stores
  async function tx<R, N extends readonly StoreNames<S>[], M extends RWMode>(
    stores: N,
    mode: M,
    runner: (transaction: IDBPTransaction<S, N, M>) => Promise<R>
  ): Promise<R>;
  // Implementation
  async function tx<R, N extends StoreNames<S> | readonly StoreNames<S>[], M extends RWMode>(
    stores: N,
    mode: M,
    runner: (transaction: IDBPTransaction<S, N extends readonly StoreNames<S>[] ? N : [N], M>) => Promise<R>
  ): Promise<R> {
    const db = await open();

    type StoreList = N extends readonly StoreNames<S>[] ? N : [N];
    const storeList = (Array.isArray(stores) ? stores : [stores]) as StoreList;
    if (storeList.length === 0) {
      throw new Error('At least one object store must be specified for the transaction');
    }

    const dbTransaction = db.transaction(storeList, mode) as IDBPTransaction<S, StoreList, RWMode>;

    try {
      const result = await runner(dbTransaction as unknown as IDBPTransaction<S, StoreList, M>);
      await dbTransaction.done;
      return result;
    } catch (error) {
      try {
        dbTransaction.abort();
      } catch {
        /* ignore abort errors */
      }
      throw error;
    }
  }

  async function put<N extends StoreNames<S>>(store: N, value: StoreValue<S, N>, key?: StoreKey<S, N>) {
    return tx(store, 'readwrite', (transaction) => {
      return transaction.objectStore(store).put(value, key);
    });
  }

  async function get<N extends StoreNames<S>>(store: N, key: StoreKey<S, N>): Promise<StoreValue<S, N> | undefined> {
    return tx(store, 'readonly', (transaction) => {
      return transaction.objectStore(store).get(key);
    });
  }

  async function deleteFn<N extends StoreNames<S>>(store: N, key: StoreKey<S, N>): Promise<void> {
    return tx(store, 'readwrite', (transaction) => {
      return transaction.objectStore(store).delete(key);
    });
  }

  async function clear<N extends StoreNames<S>>(store: N): Promise<void> {
    return tx(store, 'readwrite', (transaction) => {
      return transaction.objectStore(store).clear();
    });
  }

  async function getAllKeys<N extends StoreNames<S>>(store: N): Promise<StoreKey<S, N>[]> {
    return tx(store, 'readonly', (transaction) => {
      return transaction.objectStore(store).getAllKeys();
    });
  }

  async function getAll<N extends StoreNames<S>>(store: N): Promise<StoreValue<S, N>[]> {
    return tx(store, 'readonly', (transaction) => {
      return transaction.objectStore(store).getAll();
    });
  }

  return {
    open,
    isOpen,
    getDB,
    close,
    put,
    get,
    delete: deleteFn,
    clear,
    getAllKeys,
    getAll,
    tx,
  };
}
